import { AlertCircle } from 'lucide-react'
import { keepPreviousData } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  useChangeApplicationStatus,
  useGetRenewalRequests,
  useCreateRenewal,
} from '@/utils/api/membership-service'
import { ChangeApplicationStatusStatus } from '@/utils/api/membership-service/models/change-application-status-status'
import type { ApplicationResponseDTO, PageApplicationResponseDTO } from '@/utils/api/membership-service/models'
import { InlineDocumentViewer } from '@/features/membership/documents/components/InlineDocumentViewer'

import { ApplicationListSkeleton } from '../applications/components/ApplicationListSkeleton'
import { createRenewalColumns } from './components/columns'
import { RenewalStatusBadge } from './components/RenewalStatusBadge'
import { parseRenewalDate, formatRenewalDate } from './utils'

export function MembershipRenewalsPage() {
  const [tablePagination, setTablePagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [proofDialogOpen, setProofDialogOpen] = useState(false)
  const [selectedRenewal, setSelectedRenewal] = useState<ApplicationResponseDTO | null>(null)
  const [activateDialogOpen, setActivateDialogOpen] = useState(false)
  const [activationTarget, setActivationTarget] = useState<ApplicationResponseDTO | null>(null)
  const [paymentReference, setPaymentReference] = useState('')
  const [amountValue, setAmountValue] = useState('')
  const [durationValue, setDurationValue] = useState('')
  const [durationUnit, setDurationUnit] = useState<'MONTHS' | 'YEARS'>('YEARS')
  const [activationErrors, setActivationErrors] = useState<{
    reference?: string
    amount?: string
    duration?: string
  }>({})

  const { pageIndex, pageSize } = tablePagination

  const renewalsQuery = useGetRenewalRequests<PageApplicationResponseDTO>(
    { page: pageIndex, size: pageSize },
    {
      query: {
        placeholderData: keepPreviousData,
        select: (response) => {
          return response.data
        },
      },
    },
  )

  const changeStatusMutation = useChangeApplicationStatus()
  const createRenewalMutation = useCreateRenewal()
  const resetActivationForm = useCallback(() => {
    setPaymentReference('')
    setAmountValue('')
    setDurationValue('')
    setDurationUnit('YEARS')
    setActivationErrors({})
  }, [])

  const renewals = renewalsQuery.data?.content ?? []
  const totalRenewals =
    typeof renewalsQuery.data?.totalElements === 'number' ? renewalsQuery.data.totalElements : renewals.length

  const isMutating = changeStatusMutation.isPending || createRenewalMutation.isPending

  const handleStatusUpdate = useCallback(
    async (
      renewal: ApplicationResponseDTO,
      status: ChangeApplicationStatusStatus,
      successMessage: string,
      errorMessage: string,
      metadata?: { termMonths?: number; amount?: number; paymentReference?: string },
    ): Promise<boolean> => {
      if (changeStatusMutation.isPending || createRenewalMutation.isPending) {
        return false
      }

      try {
        const shouldCreateRenewal =
          status === ChangeApplicationStatusStatus.APPROVED ||
          status === ChangeApplicationStatusStatus.RENEWED

        let termMonths = metadata?.termMonths
        let amount = metadata?.amount
        const paymentReferenceValue = metadata?.paymentReference?.trim()

        if (shouldCreateRenewal) {
          if (
            typeof termMonths !== 'number' ||
            !Number.isFinite(termMonths) ||
            termMonths <= 0
          ) {
            const rawRenewal = renewals.find((entry) => entry.id === renewal.id)
            termMonths = getTermMonths(
              rawRenewal?.membershipStartAt,
              rawRenewal?.membershipExpiryAt,
            )
          }

          if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
            amount = undefined
          }

          const renewalPayload: {
            termMonths?: number
            amount?: number
            paymentReference?: string
          } = {}

          if (typeof termMonths === 'number' && Number.isFinite(termMonths) && termMonths > 0) {
            renewalPayload.termMonths = termMonths
          }

          if (typeof amount === 'number' && Number.isFinite(amount) && amount > 0) {
            renewalPayload.amount = amount
          }

          if (paymentReferenceValue) {
            renewalPayload.paymentReference = paymentReferenceValue
          }

          await createRenewalMutation.mutateAsync({
            id: renewal.id!,
            data: renewalPayload,
          })

          const params: {
            status: ChangeApplicationStatusStatus
            months?: number
            amount?: number
          } = {
            status,
          }

          if (typeof renewalPayload.termMonths === 'number') {
            params.months = renewalPayload.termMonths
          }

          if (typeof renewalPayload.amount === 'number') {
            params.amount = renewalPayload.amount
          }

          await changeStatusMutation.mutateAsync({
            id: renewal.id!,
            params,
          })
        } else {
          await changeStatusMutation.mutateAsync({
            id: renewal.id!,
            params: { status },
          })
        }

        toast.success(successMessage)
        await renewalsQuery.refetch()
        return true
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unexpected error occurred'
        toast.error(`${errorMessage}: ${message}`)
        return false
      }
    },
    [changeStatusMutation, createRenewalMutation, renewals, renewalsQuery],
  )

  const handleApprove = useCallback(
    (renewal: ApplicationResponseDTO) => {
      void handleStatusUpdate(
        renewal,
        ChangeApplicationStatusStatus.APPROVED,
        'Renewal approved successfully.',
        'Unable to approve renewal',
      )
    },
    [handleStatusUpdate],
  )

  const handleReject = useCallback(
    (renewal: ApplicationResponseDTO) => {
      void handleStatusUpdate(
        renewal,
        ChangeApplicationStatusStatus.REJECTED,
        'Renewal rejected.',
        'Unable to reject renewal',
      )
    },
    [handleStatusUpdate],
  )

  const handleActivate = useCallback(
    (renewal: ApplicationResponseDTO) => {
      setActivationTarget(renewal)
      resetActivationForm()
      setActivateDialogOpen(true)
    },
    [resetActivationForm],
  )

  const handleDeactivate = useCallback(
    (renewal: ApplicationResponseDTO) => {
      void handleStatusUpdate(
        renewal,
        ChangeApplicationStatusStatus.REJECTED,
        'Membership deactivated.',
        'Unable to deactivate membership',
      )
    },
    [handleStatusUpdate],
  )

  const handleViewProof = useCallback((renewal: ApplicationResponseDTO) => {
    setSelectedRenewal(renewal)
    setProofDialogOpen(true)
  }, [])

  const closeProofDialog = useCallback(() => {
    setProofDialogOpen(false)
    setSelectedRenewal(null)
  }, [])

  const handleActivationDialogClose = useCallback(() => {
    setActivateDialogOpen(false)
    setActivationTarget(null)
    resetActivationForm()
  }, [resetActivationForm])

  const handleConfirmActivation = useCallback(async () => {
    if (!activationTarget) return

    const trimmedReference = paymentReference.trim()
    const normalizedAmount = amountValue.trim().replace(/,/g, '')
    const parsedAmount =
      normalizedAmount.length > 0 ? Number(normalizedAmount) : Number.NaN
    const trimmedDuration = durationValue.trim()
    const parsedDuration = Number(trimmedDuration)

    const nextErrors: {
      reference?: string
      amount?: string
      duration?: string
    } = {}

    if (trimmedReference.length === 0) {
      nextErrors.reference = 'Enter the payment reference.'
    }

    if (
      normalizedAmount.length === 0 ||
      !Number.isFinite(parsedAmount) ||
      parsedAmount <= 0
    ) {
      nextErrors.amount = 'Provide a positive amount.'
    }

    if (
      trimmedDuration.length === 0 ||
      !Number.isFinite(parsedDuration) ||
      parsedDuration <= 0 ||
      !Number.isInteger(parsedDuration)
    ) {
      nextErrors.duration = 'Use positive whole numbers only.'
    }

    if (Object.keys(nextErrors).length > 0) {
      setActivationErrors(nextErrors)
      return
    }

    setActivationErrors({})
    const totalMonths =
      durationUnit === 'YEARS' ? parsedDuration * 12 : parsedDuration

    const wasSuccessful = await handleStatusUpdate(
      activationTarget,
      ChangeApplicationStatusStatus.APPROVED,
      'Membership activated.',
      'Unable to activate membership',
      {
        termMonths: totalMonths,
        amount: parsedAmount,
        paymentReference: trimmedReference,
      },
    )

    if (wasSuccessful) {
      handleActivationDialogClose()
    }
  }, [
    activationTarget,
    amountValue,
    durationUnit,
    durationValue,
    handleActivationDialogClose,
    handleStatusUpdate,
    paymentReference,
  ])

  const columns = useMemo(
    () =>
      createRenewalColumns({
        onApprove: (row) => handleApprove(row),
        onReject: (row) => handleReject(row),
        onActivate: (row) => handleActivate(row),
        onDeactivate: (row) => handleDeactivate(row),
        onViewProof: (row) => handleViewProof(row),
        disableActions: isMutating,
      }),
    [handleActivate, handleApprove, handleDeactivate, handleReject, handleViewProof, isMutating],
  )

  const isInitialLoading = renewalsQuery.isLoading && !renewalsQuery.data
  const isError = renewalsQuery.isError
  const hasNoData = !isInitialLoading && !isError && totalRenewals === 0

  if (isInitialLoading) {
    return <ApplicationListSkeleton />
  }

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Membership renewals</h1>
          <p className="text-muted-foreground">
            Review renewal submissions, manage member status changes, and validate payment receipts.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Renewal queue</CardTitle>
            <CardDescription>
              Track membership periods, update approval status, and keep renewals aligned with payments received.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unable to load renewals</AlertTitle>
                <AlertDescription className="flex items-center justify-between gap-2">
                  <span>
                    {renewalsQuery.error?.response?.status === 500
                      ? 'The renewals service is currently unavailable. Please try again later.'
                      : renewalsQuery.error?.message ?? 'We could not load the renewals list.'}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => renewalsQuery.refetch()}>
                    Try again
                  </Button>
                </AlertDescription>
              </Alert>
            ) : hasNoData ? (
              <div className="flex flex-col items-center justify-center space-y-3 rounded-md border border-dashed border-muted-foreground/30 bg-muted/10 p-12 text-center">
                <AlertCircle className="h-10 w-10 text-muted-foreground" />
                <h3 className="text-lg font-semibold">No renewals yet</h3>
                <p className="text-sm text-muted-foreground">
                  When members submit renewal requests, they will appear here for review.
                </p>
              </div>
            ) : (
              <EnhancedDataTable
                data={renewals}
                columns={columns}
                enablePagination
                enableSorting
                enableGlobalFilter
                enableExport
                initialColumnVisibility={{ duration: false, startDateDisplay: false, amount: false }}
                enableColumnFilters={false}
                searchPlaceholder="Search renewals…"
                loading={renewalsQuery.isFetching && !isInitialLoading}
                paginationCallbacks={{
                  manualPagination: true,
                  totalCount: totalRenewals,
                  onPageChange: (nextPageIndex, nextPageSize) =>
                    setTablePagination((prev) => {
                      if (prev.pageIndex === nextPageIndex && prev.pageSize === nextPageSize) {
                        return prev
                      }
                      return { pageIndex: nextPageIndex, pageSize: nextPageSize }
                    }),
                  onPageSizeChange: (nextPageSize) =>
                    setTablePagination((prev) => {
                      if (prev.pageSize === nextPageSize && prev.pageIndex === 0) {
                        return prev
                      }
                      return { pageIndex: 0, pageSize: nextPageSize }
                    }),
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={proofDialogOpen} onOpenChange={(open) => (!open ? closeProofDialog() : setProofDialogOpen(open))}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Proof of payment</DialogTitle>
            <DialogDescription>
              Review the uploaded receipt before confirming the renewal outcome.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const proofOfPaymentDoc = selectedRenewal?.documents?.find(
              (doc) => doc.documentType?.toLocaleUpperCase?.() === 'PROOF OF PAYMENT'
            )
            const proofUrl = proofOfPaymentDoc?.fileUrl
            const applicantName = [
              selectedRenewal?.firstName,
              selectedRenewal?.middleName,
              selectedRenewal?.lastName,
            ]
              .filter(Boolean)
              .join(' ') || 'Unnamed applicant'

            return proofUrl ? (
              <div className="space-y-3">
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <p className="font-medium">{applicantName}</p>
                  <p className="text-muted-foreground">
                    Membership period:&nbsp;
                    <strong className="text-foreground">
                      {formatRenewalDate(selectedRenewal?.membershipStartAt ?? null)} –{' '}
                      {formatRenewalDate(selectedRenewal?.membershipExpiryAt ?? null)}
                    </strong>
                  </p>
                  <p className="flex items-center gap-2 text-muted-foreground">
                    Status:
                    <RenewalStatusBadge status={selectedRenewal?.status || ''} />
                  </p>
                </div>
                <div className="space-y-2">
                  <Button asChild variant="outline" className="w-full">
                    <a href={proofUrl} target="_blank" rel="noreferrer">
                      Open proof in new tab
                    </a>
                  </Button>
                  <InlineDocumentViewer
                    url={proofUrl}
                    title={`${applicantName} proof of payment`.trim()}
                    headerLabel="Proof of payment"
                    className="h-96 w-full"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2 rounded-md border border-dashed border-muted p-6 text-center text-sm text-muted-foreground">
                <AlertCircle className="mx-auto h-10 w-10" />
                <p>No proof of payment was provided for this renewal.</p>
              </div>
            )
          })()}
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button variant="outline" onClick={closeProofDialog}>
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={activateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleActivationDialogClose()
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Activate membership</DialogTitle>
            <DialogDescription>
              Record the payment details before activating this membership.
            </DialogDescription>
          </DialogHeader>

          {activationTarget ? (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">
                {[activationTarget.firstName, activationTarget.middleName, activationTarget.lastName]
                  .filter(Boolean)
                  .join(' ') || 'Unnamed applicant'}
              </p>
              <p className="text-muted-foreground">{activationTarget.categoryName || 'Uncategorised'}</p>
            </div>
          ) : null}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="activation-payment-reference">Payment reference</Label>
              <Input
                id="activation-payment-reference"
                value={paymentReference}
                onChange={(event) => {
                  setPaymentReference(event.target.value)
                  setActivationErrors((prev) => ({ ...prev, reference: undefined }))
                }}
                placeholder="e.g. BANK-REF-12345"
                autoComplete="off"
                disabled={isMutating}
              />
              {activationErrors.reference ? (
                <p className="text-sm text-destructive">{activationErrors.reference}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="activation-amount">Amount paid (UGX)</Label>
              <Input
                id="activation-amount"
                value={amountValue}
                onChange={(event) => {
                  setAmountValue(event.target.value)
                  setActivationErrors((prev) => ({ ...prev, amount: undefined }))
                }}
                placeholder="e.g. 250000"
                inputMode="decimal"
                autoComplete="off"
                disabled={isMutating}
              />
              {activationErrors.amount ? (
                <p className="text-sm text-destructive">{activationErrors.amount}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="activation-duration">Membership term</Label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  id="activation-duration"
                  value={durationValue}
                  onChange={(event) => {
                    setDurationValue(event.target.value)
                    setActivationErrors((prev) => ({ ...prev, duration: undefined }))
                  }}
                  placeholder="12"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={isMutating}
                  className="sm:w-32"
                />
                <Select
                  value={durationUnit}
                  onValueChange={(value) => setDurationUnit(value as 'MONTHS' | 'YEARS')}
                  disabled={isMutating}
                >
                  <SelectTrigger className="sm:w-40">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTHS">Months</SelectItem>
                    <SelectItem value="YEARS">Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {activationErrors.duration ? (
                <p className="text-sm text-destructive">{activationErrors.duration}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  We will convert years to months automatically before submission.
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button variant="outline" onClick={handleActivationDialogClose} disabled={isMutating}>
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={handleConfirmActivation} disabled={isMutating}>
              {isMutating ? 'Activating…' : 'Activate membership'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function getTermMonths(
  startDate?: string | null,
  endDate?: string | null,
): number | undefined {
  const start = parseRenewalDate(startDate ?? null)
  const end = parseRenewalDate(endDate ?? null)

  if (!start || !end) {
    return undefined
  }

  const diffMs = end.getTime() - start.getTime()
  if (diffMs <= 0) {
    return undefined
  }

  const totalDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1
  const months = Math.floor(totalDays / 30)

  if (months <= 0) {
    return 1
  }

  return months
}
